import * as React from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";

// Password input with an inline show/hide toggle ("viewfinder"). Behaves
// exactly like the standard <Input type="password"> from @/components/ui/Input
// — same height, focus ring, theme tokens — but adds an eye button on the
// right that flips type between "password" and "text".
//
// Accessibility:
//   - The toggle is type="button" so it never submits the surrounding form.
//   - aria-pressed reflects the current visibility state.
//   - aria-label reads "Show password" / "Hide password" so screen readers
//     announce the action rather than the icon.
//
// Drop-in replacement: anywhere we used <Input type="password" {...register(...)} />
// we can now use <PasswordInput {...register(...)} /> with no other changes.
export const PasswordInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  // We strip `type` out of props deliberately — the visibility toggle owns
  // the type attribute, and a caller passing type=text would defeat masking.
  ({ className, type: _ignoredType, ...props }, ref) => {
    const [visible, setVisible] = React.useState(false);
    return (
      <div className="relative">
        <input
          ref={ref}
          {...props}
          type={visible ? "text" : "password"}
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 pr-10 text-sm",
            "placeholder:text-muted-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-pressed={visible}
          aria-label={visible ? "Hide password" : "Show password"}
          tabIndex={-1}
          className={cn(
            "absolute right-1 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = "PasswordInput";
